import { callSafetyInstructionChatApiWithCallback } from "@/api/ChatApi";

// この機能を表示する許可ユーザー（メールアドレス、小文字で比較）
const ALLOWED_EMAILS = [
  "sh-kudo@daiho.jp",
  "shunsuke.wada@wonder-soft.com",
  "t-aoyama@daiho.jp",
];

/**
 * 安全指示事項チャットコンポーネント
 *
 * d-prompt-manager の chat?chatType=latest_diary_search 機能を呼び出し、
 * ユーザーが安全指示の文言を質問するとおすすめの安全指示事項を提示する。
 * ユーザーが「反映」を押すと apply-instruction イベントで親に通知する。
 *
 * 本機能は ALLOWED_EMAILS に含まれるユーザーにのみ表示する。
 */
export default {
  name: "SafetyInstructionChat",
  props: {
    // 作業内容モーダルが閲覧専用のときは反映ボタンを無効化する
    disabled: {
      type: Boolean,
      default: false,
    },
    // 工種（送信時の質問のヒントに利用）
    workType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isOpen: false,
      inputText: "",
      isStreaming: false,
      messages: [],
      recommendedItems: [],
    };
  },
  computed: {
    // 許可された特定ユーザーのみに機能を表示する
    isAllowedUser() {
      const email =
        (this.$auth && this.$auth.user && this.$auth.user.email) || "";
      return ALLOWED_EMAILS.includes(email.toLowerCase());
    },
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    onEnterKey(event) {
      // IME変換中（日本語入力の変換確定）のEnterでは送信しない
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      event.preventDefault();
      this.sendMessage();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageArea;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    sendMessage() {
      const question = this.inputText.trim();
      if (!question || this.isStreaming) {
        return;
      }

      // ユーザーメッセージを追加
      this.messages.push({ role: "user", content: question });
      // アシスタントの回答用プレースホルダを追加
      this.messages.push({ role: "assistant", content: "" });
      const assistantIndex = this.messages.length - 1;

      this.inputText = "";
      this.recommendedItems = [];
      this.isStreaming = true;
      this.scrollToBottom();

      callSafetyInstructionChatApiWithCallback(
        question,
        // チャンクごとのコールバック
        (chunk) => {
          this.handleChunk(chunk, assistantIndex);
        },
        // 完了時のコールバック
        () => {
          this.isStreaming = false;
          // 何も返ってこなかった場合のフォールバック表示
          if (!this.messages[assistantIndex].content) {
            this.$set(
              this.messages,
              assistantIndex,
              {
                role: "assistant",
                content: "該当する安全指示事項が見つかりませんでした。",
              }
            );
          }
          this.scrollToBottom();
        },
        // エラー時のコールバック
        (error) => {
          console.error("安全指示チャットでエラーが発生しました:", error);
          this.isStreaming = false;
          // 利用可能な理由があればそれを出す。権限を落とされている場合など、
          // 「エラーが発生しました」だけでは利用者が原因に辿り着けない。
          const reason = (error && error.userMessage) || "";
          this.$set(this.messages, assistantIndex, {
            role: "assistant",
            content: reason || "エラーが発生しました。もう一度お試しください。",
          });
          this.$bvToast.toast(
            reason || "AIからの回答取得中にエラーが発生しました。",
            {
              title: "エラー",
              variant: "danger",
              solid: true,
            }
          );
        }
      );
    },
    handleChunk(chunk, assistantIndex) {
      let data = chunk;
      try {
        data = typeof chunk === "string" ? JSON.parse(chunk) : chunk;
      } catch (e) {
        // パースできない場合は文字列としてそのまま追記
        if (typeof chunk === "string") {
          this.appendAssistantToken(assistantIndex, chunk);
          this.scrollToBottom();
        }
        return;
      }

      const result = data && data.result ? data.result : null;
      if (!result) {
        return;
      }

      // 最初のメタデータチャンクは無視
      if (result.chatMetadata) {
        return;
      }

      // エラーの場合
      if (result.errorCode || result.errorMessage) {
        this.appendAssistantToken(
          assistantIndex,
          result.errorMessage || "エラーが発生しました。"
        );
        this.scrollToBottom();
        return;
      }

      // おすすめ安全指示事項の検索結果
      if (
        result.safetyInstructionSearchResult &&
        Array.isArray(result.safetyInstructionSearchResult.items)
      ) {
        this.recommendedItems = result.safetyInstructionSearchResult.items;
        this.scrollToBottom();
      }

      // ストリーミングテキスト（思考トークンは除外）
      if (result.token && result.tokenType !== "THINKING") {
        this.appendAssistantToken(assistantIndex, result.token);
        this.scrollToBottom();
      }
    },
    appendAssistantToken(assistantIndex, token) {
      const current = this.messages[assistantIndex];
      this.$set(this.messages, assistantIndex, {
        role: "assistant",
        content: (current.content || "") + token,
      });
    },
    applyInstruction(item) {
      if (this.disabled) {
        return;
      }
      this.$emit("apply-instruction", {
        // 安全指示事項は種別「安全」として反映する
        type: "safety",
        instruction: item.instruction,
      });
      this.$bvToast.toast("安全指示事項を反映しました。", {
        title: "反映",
        variant: "success",
        solid: true,
        autoHideDelay: 2000,
      });
    },
  },
};
